import { useEffect, useRef } from 'react'
import { useRestaurantStore, filterHiddenFromMenu, isRetriableRequestError } from '@restaurant/core'
import type { RawMenuResponse } from '@restaurant/core'
import { apiClient } from '@restaurant/api-client'
import { safeGetLogger } from '@restaurant/logging'
import { isShowcase } from '../showcase/showcase'
import { transformMenuResponse, type RawMenuDataItem } from '../lib/menuTransform'

/** Backoff before each retry. Length also caps the retry count. */
const RETRY_DELAYS_MS = [1000, 3000]

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Hook to fetch and populate menu data from Flask API.
 * Fetches menu items and categories for the current restaurant's store, with
 * retry-with-backoff on a transient failure. Returns void — the only loading
 * signal callers should read is the shared store's `isLoadingMenu`.
 */
export function useMenuData() {
  // Subscribe to the id, not the object — the effect must re-run exactly when
  // the diner's store changes, and the ORDER path uses currentLocation
  // (order.service reads currentLocation.merchantId), so the menu must too.
  // locations[0] was flatly wrong for multi-location tenants: main.tsx's
  // prefetch loads the geolocated-closest store's menu and this hook then
  // clobbered it with store 0's.
  const currentLocationId = useRestaurantStore((s) => s.currentLocation?.id)
  const setCategories = useRestaurantStore((s) => s.setCategories)
  // Run token: bumped synchronously at the top of every effect run (including
  // ones that dedupe and return early). Lets a run's `finally` tell whether it
  // is still the latest run before it clears the shared isLoadingMenu flag —
  // see the finally block below for why this matters.
  const runIdRef = useRef(0)

  useEffect(() => {
    if (isShowcase()) return
    const runId = ++runIdRef.current
    const fetchMenuData = async () => {
      const storeId = currentLocationId
      if (!storeId) {
        safeGetLogger()?.debug('No store ID, skipping menu fetch', { domain: 'menu' })
        return
      }
      // Dedupe against the main.tsx prefetch (or a previous run): the store
      // records which store the loaded categories belong to. Read at fetch
      // time via getState() — subscribing would re-run this effect on its own
      // write. null provenance (showcase/tests) always allows the fetch.
      if (useRestaurantStore.getState().menuStoreId === storeId) {
        safeGetLogger()?.debug('Menu already loaded for store, skipping', { domain: 'menu', storeId })
        // This run just superseded any older run (its token is newest), so an
        // older in-flight fetch's `finally` will no longer be allowed to clear
        // isLoadingMenu (see below). But THIS store's data is already loaded
        // and on-screen — if a skeleton is showing, it's stuck. Clear it here.
        if (useRestaurantStore.getState().isLoadingMenu) {
          useRestaurantStore.getState().setLoadingMenu(false)
        }
        return
      }

      safeGetLogger()?.debug('Fetching menu', { domain: 'menu', storeId })

      useRestaurantStore.getState().setLoadingMenu(true)

      try {
        // isLoadingMenu stays true across every retry attempt below — the
        // MenuPage skeleton should persist through backoff, not flicker.
        for (let attempt = 0; ; attempt++) {
          try {
            // Call Flask endpoint to get menu items by store ID
            const rawResponse = await apiClient.post<RawMenuResponse>('/stores/menu-items', {
              store_ids: [storeId],
              use_optimized: false
            })
            const response = filterHiddenFromMenu(rawResponse) as unknown as Record<string, RawMenuDataItem[]>

            const categories = transformMenuResponse(response, storeId)

            safeGetLogger()?.debug('Menu categories processed', { domain: 'menu', count: categories.length })

            // Latest-wins: if the diner switched locations while this fetch was
            // in flight, this response describes the WRONG store — drop it. The
            // effect run keyed to the new id owns the store now.
            if (useRestaurantStore.getState().currentLocation?.id !== storeId) {
              safeGetLogger()?.debug('Dropping stale menu response', { domain: 'menu', storeId })
              return
            }
            setCategories(categories, storeId)
            break

          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load menu'

            // Undefined once `attempt` runs past the end of the table — that is
            // the retry budget expiring.
            const retryDelayMs = RETRY_DELAYS_MS[attempt]
            if (retryDelayMs !== undefined && isRetriableRequestError(err)) {
              safeGetLogger()?.warn('Menu load failed, retrying', {
                domain: 'menu', error: errorMessage, attempt: attempt + 1,
              })
              // A newer run may have taken over the store while we were about
              // to sleep, or while we slept — bail silently either way and
              // leave the store to its new owner.
              // Belt-and-braces: this pre-sleep check just skips a pointless
              // backoff when we're already superseded. The load-bearing,
              // test-covered check is the one AFTER sleep below.
              if (runIdRef.current !== runId) return
              await sleep(retryDelayMs)
              if (runIdRef.current !== runId) return
              continue
            }

            safeGetLogger()?.error('Menu load failed', {
              domain: 'menu', error: errorMessage, attempts: attempt + 1,
            })
            break
          }
        }
      } finally {
        // Only the LATEST run may clear the loading flag: a stale fetch's
        // finally firing early would drop MenuPage's skeleton and expose the
        // previous store's grid — tappable, and wrong-store items could land
        // in the cart. The token bumps synchronously on every effect run
        // (including ones that dedupe and return early above), so a
        // superseded run always sees a newer token here and leaves the flag
        // to its owner (or to the dedupe early-return, which clears it itself
        // when it supersedes an in-flight fetch — see above).
        if (runIdRef.current === runId) {
          useRestaurantStore.getState().setLoadingMenu(false)
        }
      }
    }

    fetchMenuData()
  }, [currentLocationId, setCategories])
}
